import { Box, Stack, Typography } from '@mui/material'
import SectionContainer from './SectionContainer'
import { SectionTemplate } from '../types'
import { pathColor, scoreColor, scoreBackground } from '../palette'

interface PathLite {
  title?: string
  fitScore?: number
  oneLineDescription?: string
}

interface Props {
  template: SectionTemplate
  values: { paths?: PathLite[] }
  title: string
  description: string
  sectionNumber?: number
}

function ScoreBadge({ score }: { score?: number }) {
  const s = typeof score === 'number' ? Math.max(0, Math.min(100, score)) : 0
  const color = scoreColor(s)
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 0.5,
        px: 1.75,
        py: 0.75,
        border: `1.5px solid ${color}`,
        color,
        background: scoreBackground(s),
        fontFamily: 'var(--ns-display-font)',
        fontSize: 24,
        fontWeight: 500,
        borderRadius: 0.5,
      }}
    >
      {s}
      <Typography component="span" variant="caption" sx={{ color, opacity: 0.7 }}>
        / 100
      </Typography>
    </Box>
  )
}

export default function ComparisonSection({ template, values, title, description, sectionNumber }: Props) {
  const paths = values?.paths || []
  return (
    <SectionContainer
      id={template.sectionTypeId}
      number={sectionNumber}
      title={title}
      description={description}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: `repeat(${Math.min(paths.length, 3)}, 1fr)`,
          },
          gap: 3,
        }}
      >
        {paths.map((path, i) => {
          const accent = pathColor(i)
          return (
            <Box
              key={i}
              sx={{
                position: 'relative',
                p: 3,
                pt: 4,
                background: 'rgba(255,255,255,0.7)',
                border: 1,
                borderColor: 'divider',
                borderTop: `4px solid ${accent}`,
                transition: 'transform 200ms ease, box-shadow 200ms ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                },
              }}
            >
              <Stack spacing={2}>
                <Typography
                  variant="caption"
                  sx={{
                    color: accent,
                    letterSpacing: 2,
                    fontWeight: 700,
                    fontSize: 11,
                  }}
                >
                  PATH {String(i + 1).padStart(2, '0')}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'var(--ns-display-font)',
                    fontSize: { xs: 22, md: 26 },
                    fontWeight: 500,
                    lineHeight: 1.15,
                    minHeight: 80,
                  }}
                >
                  {path.title}
                </Typography>
                <Box>
                  <ScoreBadge score={path.fitScore} />
                </Box>
                {path.oneLineDescription && (
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {path.oneLineDescription}
                  </Typography>
                )}
              </Stack>
            </Box>
          )
        })}
      </Box>
    </SectionContainer>
  )
}
